import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  ViewEncapsulation,
  computed,
  inject,
  input,
  signal
} from '@angular/core';

export type TooltipPosition = 'top' | 'right' | 'down' | 'left';
export type TooltipTriggerMode = 'hover' | 'click' | 'both';

@Component({
  selector: 'ush-tooltip',
  standalone: true,
  imports: [OverlayModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <span
      cdkOverlayOrigin
      #trigger="cdkOverlayOrigin"
      class="ush-tooltip__trigger"
      (mouseenter)="handleMouseEnter()"
      (mouseleave)="handleMouseLeave()"
      (focusin)="handleFocusIn()"
      (focusout)="handleFocusOut()"
      (click)="handleTriggerClick()"
    >
      <ng-content select=":not([ushTooltipContent])"></ng-content>
    </span>

    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayOrigin]="trigger"
      [cdkConnectedOverlayOpen]="isOpen()"
      [cdkConnectedOverlayPositions]="connectedPositions()"
      [cdkConnectedOverlayViewportMargin]="8"
      [cdkConnectedOverlayPush]="true"
    >
      <span
        class="ush-tooltip__content"
        [class.ush-tooltip__content--top]="position() === 'top'"
        [class.ush-tooltip__content--right]="position() === 'right'"
        [class.ush-tooltip__content--down]="position() === 'down'"
        [class.ush-tooltip__content--left]="position() === 'left'"
        [id]="tooltipId()"
        role="tooltip"
      >
        <ng-content select="[ushTooltipContent]"></ng-content>
      </span>
    </ng-template>
  `,
  styles: [
    `
      .cdk-overlay-container,
      .cdk-global-overlay-wrapper {
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
      }

      .cdk-overlay-container {
        position: fixed;
        z-index: 2147483646 !important;
      }

      .cdk-overlay-container:empty {
        display: none;
      }

      .cdk-global-overlay-wrapper {
        display: flex;
        position: absolute;
        z-index: 2147483646 !important;
      }

      .cdk-overlay-pane {
        display: flex;
        position: absolute;
        z-index: 2147483646 !important;
        box-sizing: border-box;
        max-width: 100%;
        max-height: 100%;
        pointer-events: auto;
      }

      .cdk-overlay-connected-position-bounding-box {
        display: flex;
        position: absolute;
        flex-direction: column;
        z-index: 2147483646 !important;
        min-width: 1px;
        min-height: 1px;
      }

      ush-tooltip {
        display: inline-flex;
        position: relative;
        align-items: center;
      }

      .ush-tooltip__trigger {
        display: inline-flex;
        align-items: center;
      }

      .ush-tooltip__content {
        display: block;
        position: relative;
        z-index: 2147483646;
        border-radius: 2px;
        background: var(--ush-tooltip-background, #3f4951);
        padding: 8px;
        width: var(--ush-tooltip-width, 195px);
        max-width: min(var(--ush-tooltip-width, 195px), calc(100vw - 32px));
        pointer-events: none;
        color: var(--ush-tooltip-color, #ffffff);
        font-weight: 400;
        font-size: 16px;
        line-height: 1.15;
        white-space: normal;
        word-break: normal;
        overflow-wrap: break-word;
      }

      .ush-tooltip__content::before {
        position: absolute;
        border-style: solid;
        width: 0;
        height: 0;
        content: '';
      }

      .ush-tooltip__content--right {
        margin-left: 0;
      }

      .ush-tooltip__content--right::before {
        top: 50%;
        left: -8px;
        transform: translateY(-50%);
        border-width: 8px 8px 8px 0;
        border-color: transparent var(--ush-tooltip-background, #3f4951) transparent transparent;
      }

      .ush-tooltip__content--left {
        margin-right: 0;
      }

      .ush-tooltip__content--left::before {
        top: 50%;
        right: -8px;
        transform: translateY(-50%);
        border-width: 8px 0 8px 8px;
        border-color: transparent transparent transparent var(--ush-tooltip-background, #3f4951);
      }

      .ush-tooltip__content--top {
        margin-bottom: 0;
      }

      .ush-tooltip__content--top::before {
        bottom: -8px;
        left: 50%;
        transform: translateX(-50%);
        border-width: 8px 8px 0 8px;
        border-color: var(--ush-tooltip-background, #3f4951) transparent transparent transparent;
      }

      .ush-tooltip__content--down {
        margin-top: 0;
      }

      .ush-tooltip__content--down::before {
        top: -8px;
        left: 50%;
        transform: translateX(-50%);
        border-width: 0 8px 8px 8px;
        border-color: transparent transparent var(--ush-tooltip-background, #3f4951) transparent;
      }
    `
  ]
})
export class TooltipComponent {
  readonly position = input<TooltipPosition>('right');
  readonly tooltipId = input<string | null>(null);
  readonly triggerMode = input<TooltipTriggerMode>('hover');
  readonly hideDelayMs = input<number>(50);

  protected readonly isOpen = signal(false);
  private readonly isHoverActive = signal(false);
  private readonly isClickActive = signal(false);
  private hideTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  protected readonly connectedPositions = computed(() => this.getConnectedPositions(this.position()));

  ngOnDestroy(): void {
    this.clearHideTimeout();
  }

  protected handleMouseEnter(): void {
    if (!this.supportsHover()) {
      return;
    }

    this.isHoverActive.set(true);
    this.show();
  }

  protected handleMouseLeave(): void {
    if (!this.supportsHover()) {
      return;
    }

    this.isHoverActive.set(false);

    if (!this.isClickActive()) {
      this.scheduleHide();
    }
  }

  protected handleFocusIn(): void {
    this.handleMouseEnter();
  }

  protected handleFocusOut(): void {
    this.handleMouseLeave();
  }

  protected handleTriggerClick(): void {
    if (!this.supportsClick()) {
      return;
    }

    const nextClickState = !this.isClickActive();
    this.isClickActive.set(nextClickState);

    if (nextClickState) {
      this.show();
      return;
    }

    if (!this.isHoverActive()) {
      this.scheduleHide();
    }
  }

  @HostListener('document:click', ['$event'])
  protected handleDocumentClick(event: Event): void {
    if (!this.supportsClick() || !this.isOpen()) {
      return;
    }

    const eventTarget = event.target as Node | null;
    if (eventTarget && this.hostElement.nativeElement.contains(eventTarget)) {
      return;
    }

    this.isClickActive.set(false);

    if (!this.isHoverActive()) {
      this.scheduleHide();
    }
  }

  private supportsHover(): boolean {
    return this.triggerMode() === 'hover' || this.triggerMode() === 'both';
  }

  private supportsClick(): boolean {
    return this.triggerMode() === 'click' || this.triggerMode() === 'both';
  }

  private show(): void {
    this.clearHideTimeout();
    this.isOpen.set(true);
  }

  private scheduleHide(): void {
    this.clearHideTimeout();

    const delay = this.hideDelayMs();
    if (delay <= 0) {
      this.isOpen.set(false);
      return;
    }

    this.hideTimeoutId = setTimeout(() => {
      this.hideTimeoutId = null;
      if (!this.isHoverActive() && !this.isClickActive()) {
        this.isOpen.set(false);
      }
    }, delay);
  }

  private clearHideTimeout(): void {
    if (this.hideTimeoutId) {
      clearTimeout(this.hideTimeoutId);
      this.hideTimeoutId = null;
    }
  }

  private getConnectedPositions(position: TooltipPosition): ConnectedPosition[] {
    switch (position) {
      case 'top':
        return [
          {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom',
            offsetY: -8
          }
        ];
      case 'down':
        return [
          {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
            offsetY: 8
          }
        ];
      case 'left':
        return [
          {
            originX: 'start',
            originY: 'center',
            overlayX: 'end',
            overlayY: 'center',
            offsetX: -8
          }
        ];
      case 'right':
      default:
        return [
          {
            originX: 'end',
            originY: 'center',
            overlayX: 'start',
            overlayY: 'center',
            offsetX: 8
          }
        ];
    }
  }
}
